import type { Identifier } from "ra-core";

export type TeamAttachment = { src: string; title: string; type?: string };

/** A row of the gula_team_feed view. */
export interface TeamMessage {
  id: Identifier;
  created_at: string;
  sales_id: Identifier | null;
  kind: "message" | "task" | "event";
  event?: string | null;
  body: string;
  contact_id?: Identifier | null;
  client_id?: Identifier | null;
  contact_name?: string | null;
  assignee_id?: Identifier | null;
  assignee_name?: string | null;
  due_date?: string | null;
  done_at?: string | null;
  done_by_name?: string | null;
  author_name?: string | null;
  attachments?: TeamAttachment[] | null;
}

export type TeamFilter = "all" | "mine" | "pending";

const sameId = (a?: Identifier | null, b?: Identifier | null) =>
  a != null && b != null && String(a) === String(b);

export const isOpenTask = (message: TeamMessage) =>
  message.kind === "task" && !message.done_at;

export const filterMessages = (
  messages: TeamMessage[],
  filter: TeamFilter,
  meId?: Identifier | null,
) => {
  if (filter === "pending") return messages.filter(isOpenTask);
  if (filter === "mine")
    return messages.filter(
      (message) => isOpenTask(message) && sameId(message.assignee_id, meId),
    );
  return messages;
};

/** Messages written by others (or by the CRM) after the last visit. */
export const unreadCount = (
  messages: Pick<TeamMessage, "created_at" | "sales_id">[],
  lastReadAt: string | null | undefined,
  meId?: Identifier | null,
) =>
  messages.filter(
    (message) =>
      !sameId(message.sales_id, meId) &&
      (!lastReadAt || message.created_at > lastReadAt),
  ).length;

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** "Hoy", "Ayer", "Lunes 19 de octubre". */
export const dayLabel = (iso: string, now = new Date()) => {
  const date = new Date(iso);
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (days === 0) return "Hoy";
  if (days === 1) return "Ayer";
  const text = date.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** Oldest first, grouped by day, as a chat reads. */
export const groupByDay = (messages: TeamMessage[], now = new Date()) => {
  const sorted = [...messages].sort((a, b) =>
    a.created_at.localeCompare(b.created_at),
  );
  const groups: { label: string; messages: TeamMessage[] }[] = [];
  for (const message of sorted) {
    const label = dayLabel(message.created_at, now);
    const last = groups[groups.length - 1];
    if (last?.label === label) last.messages.push(message);
    else groups.push({ label, messages: [message] });
  }
  return groups;
};

export const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
